/**
 * Reusable modal shell.
 *
 * Closes on:
 *   - clicking the × button
 * By default does NOT close on backdrop click or Escape (disableBackdropClose=true).
 *
 * Children render inside .modal-panel so they don't need to know about the
 * overlay/positioning concerns.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function Modal({ open, title, onClose, children, footer, size, disableBackdropClose = true, headerExtra, className }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !disableBackdropClose) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, disableBackdropClose]);

  if (!open) return null;

  // Portal straight to document.body: a caller that opens this modal from
  // inside an ancestor with backdrop-filter/filter/transform (e.g. the
  // sticky app-header, which blurs its background) would otherwise have
  // this modal's position:fixed resolve against that ancestor instead of
  // the viewport — collapsing the backdrop to the ancestor's own size.
  return createPortal(
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !disableBackdropClose) onClose?.();
      }}
    >
      <div className={`modal-panel${size ? ` modal-panel--${size}` : ''}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-header">
          <h2>{title}</h2>
          <div className="modal-header-right">
            {headerExtra}
            <button className="modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
